import React from 'react';
import { MapContainer, TileLayer, Marker, Popup } from 'react-leaflet';
import L from 'leaflet';
import 'leaflet/dist/leaflet.css';

// Fix Leaflet default marker icon
delete L.Icon.Default.prototype._getIconUrl;
L.Icon.Default.mergeOptions({
  iconRetinaUrl: 'https://cdnjs.cloudflare.com/ajax/libs/leaflet/1.9.4/images/marker-icon-2x.png',
  iconUrl: 'https://cdnjs.cloudflare.com/ajax/libs/leaflet/1.9.4/images/marker-icon.png',
  shadowUrl: 'https://cdnjs.cloudflare.com/ajax/libs/leaflet/1.9.4/images/marker-shadow.png',
});

const customIcon = new L.Icon({
  iconUrl: 'https://cdnjs.cloudflare.com/ajax/libs/leaflet/1.9.4/images/marker-icon.png',
  iconRetinaUrl: 'https://cdnjs.cloudflare.com/ajax/libs/leaflet/1.9.4/images/marker-icon-2x.png',
  shadowUrl: 'https://cdnjs.cloudflare.com/ajax/libs/leaflet/1.9.4/images/marker-shadow.png',
  iconSize: [25, 41],
  iconAnchor: [12, 41],
  popupAnchor: [1, -34],
  shadowSize: [41, 41],
});

// Stable style refs — extracted to module scope so MapContainer doesn't get a
// fresh `style` object on every render (Leaflet re-initializes if props churn).
const MAP_FILL_STYLE = { height: '100%', width: '100%' };

/**
 * Single location map for a listing detail page
 */
export const ListingMap = ({ latitude, longitude, title, address, className = '', height = '250px' }) => {
  if (!latitude || !longitude) return null;
  const lat = parseFloat(latitude);
  const lng = parseFloat(longitude);
  if (isNaN(lat) || isNaN(lng)) return null;

  const directionsUrl = `https://www.google.com/maps/dir/?api=1&destination=${lat},${lng}`;

  return (
    <div className={`rounded-xl overflow-hidden border border-slate-200 ${className}`} style={{ height }} data-testid="listing-map">
      <MapContainer center={[lat, lng]} zoom={15} style={MAP_FILL_STYLE} scrollWheelZoom={false}>
        <TileLayer
          attribution='&copy; <a href="https://www.openstreetmap.org/copyright">OpenStreetMap</a>'
          url="https://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png"
        />
        <Marker position={[lat, lng]} icon={customIcon}>
          <Popup>
            <div className="text-sm">
              <p className="font-semibold">{title}</p>
              {address && <p className="text-xs text-slate-500 mt-0.5">{address}</p>}
              <a href={directionsUrl} target="_blank" rel="noopener noreferrer"
                className="mt-1.5 inline-block text-xs font-semibold text-white bg-gradient-to-br from-sky-500 via-blue-500 to-indigo-600 hover:from-sky-600 hover:to-indigo-700 px-2.5 py-1 rounded-full transition-colors no-underline">
                ➤ Get Directions
              </a>
            </div>
          </Popup>
        </Marker>
      </MapContainer>
    </div>
  );
};

/**
 * Multi-marker map for search results
 */
export const SearchMap = ({ listings = [], onMarkerClick, className = '', height = '400px' }) => {
  const validListings = listings.filter(l => l.latitude && l.longitude && !isNaN(parseFloat(l.latitude)) && !isNaN(parseFloat(l.longitude)));
  if (validListings.length === 0) return null;

  const bounds = validListings.map(l => [parseFloat(l.latitude), parseFloat(l.longitude)]);
  const center = [
    bounds.reduce((s, b) => s + b[0], 0) / bounds.length,
    bounds.reduce((s, b) => s + b[1], 0) / bounds.length,
  ];

  return (
    <div className={`rounded-xl overflow-hidden border border-slate-200 ${className}`} style={{ height }} data-testid="search-map">
      <MapContainer center={center} zoom={12} style={MAP_FILL_STYLE} scrollWheelZoom={true} bounds={bounds.length > 1 ? bounds : undefined}>
        <TileLayer
          attribution='&copy; <a href="https://www.openstreetmap.org/copyright">OpenStreetMap</a>'
          url="https://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png"
        />
        {validListings.map(listing => (
          <Marker key={listing.listing_id} position={[parseFloat(listing.latitude), parseFloat(listing.longitude)]} icon={customIcon}>
            <Popup>
              <div className="text-sm min-w-[180px]">
                <p className="font-semibold text-slate-900">{listing.title}</p>
                <p className="text-xs text-slate-500 mt-0.5">{listing.address}</p>
                <div className="flex items-center justify-between mt-1.5">
                  <span className="text-xs font-bold text-blue-600">
                    {listing.currency_symbol || '₹'}{listing.base_price}/{listing.pricing_model === 'hourly' ? 'hr' : listing.pricing_model === 'daily' ? 'day' : listing.pricing_model}
                  </span>
                  {listing.average_rating > 0 && (
                    <span className="text-xs text-amber-600">★ {listing.average_rating}</span>
                  )}
                </div>
                <button
                  onClick={() => onMarkerClick && onMarkerClick(listing)}
                  className="mt-1.5 w-full text-xs bg-blue-500 hover:bg-blue-600 text-white py-1 px-2 rounded font-medium transition-colors">
                  View Details
                </button>
                <a
                  href={`https://www.google.com/maps/dir/?api=1&destination=${parseFloat(listing.latitude)},${parseFloat(listing.longitude)}`}
                  target="_blank" rel="noopener noreferrer"
                  className="mt-1 w-full text-xs bg-gradient-to-br from-sky-500 via-blue-500 to-indigo-600 hover:from-sky-600 hover:to-indigo-700 text-white py-1 px-2 rounded font-semibold transition-colors flex items-center justify-center gap-1 no-underline">
                  ➤ Directions
                </a>
              </div>
            </Popup>
          </Marker>
        ))}
      </MapContainer>
    </div>
  );
};
